import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, makeStyles, Tooltip, Typography
} from "@material-ui/core";
import { AddPhotoAlternate, DeleteOutline, Send } from "@material-ui/icons";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  drop: { border: `2px dashed ${theme.palette.primary.main}`, borderRadius: 16, padding: 20, textAlign: "center", background: theme.palette.action.hover },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(120px,1fr))", gap: 14, marginTop: 18 },
  card: { position: "relative", minHeight: 130, borderRadius: 14, padding: 10, background: theme.palette.background.default, border: `1px solid ${theme.palette.divider}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between" },
  image: { width: 90, height: 90, objectFit: "contain", cursor: "pointer" },
  remove: { position: "absolute", right: 2, top: 2, background: theme.palette.background.paper }
}));

const toWebp = async file => {
  if (file.type === "image/webp") return new File([file], file.name.replace(/\.[^.]+$/, "") + ".webp", { type: "image/webp" });
  if (!file.type.startsWith("image/")) throw new Error("Selecione uma imagem válida");
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, 512 / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise((resolve, reject) => canvas.toBlob(value => value ? resolve(value) : reject(new Error("Falha ao converter imagem")), "image/webp", 0.96));
  bitmap.close?.();
  return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".webp", { type: "image/webp" });
};

export default function StickerLibraryDialog({ open, onClose, onSelect, canDelete }) {
  const classes = useStyles();
  const [stickers, setStickers] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try { setLoading(true); setStickers((await api.get("/company-stickers")).data); }
    catch (err) { toastError(err); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const upload = async files => {
    for (const input of Array.from(files || [])) {
      try {
        setLoading(true);
        const file = await toWebp(input);
        const data = new FormData();
        data.append("sticker", file, file.name);
        data.append("name", input.name.replace(/\.[^.]+$/, ""));
        await api.post("/company-stickers?typeArch=stickers", data);
      } catch (err) { toastError(err); }
    }
    await load();
  };
  const choose = async sticker => {
    try {
      setLoading(true);
      const response = await api.get(sticker.url, { responseType: "blob" });
      onSelect(new File([response.data], `sticker__${sticker.fileName}`, { type: "image/webp" }));
      onClose();
    } catch (err) { toastError(err); setLoading(false); }
  };
  const remove = async sticker => {
    try { await api.delete(`/company-stickers/${sticker.id}`); await load(); }
    catch (err) { toastError(err); }
  };

  return <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>Biblioteca de figurinhas da empresa</DialogTitle>
    <DialogContent dividers>
      <Box className={classes.drop} onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); upload(e.dataTransfer.files); }}>
        <AddPhotoAlternate color="primary" fontSize="large" />
        <Typography variant="h6">Arraste imagens ou figurinhas aqui</Typography>
        <Typography variant="body2" color="textSecondary">Imagens são convertidas para WebP em alta qualidade, no tamanho recomendado pelo WhatsApp.</Typography>
        <Button component="label" color="primary" variant="outlined" style={{ marginTop: 12 }}>
          Adicionar figurinhas<input hidden multiple type="file" accept="image/*,.webp" onChange={e => upload(e.target.files)} />
        </Button>
      </Box>
      {loading && <Box textAlign="center" p={2}><CircularProgress size={28} /></Box>}
      <Box className={classes.grid}>
        {stickers.map(sticker => <Box className={classes.card} key={sticker.id}>
          {canDelete && <Tooltip title="Excluir da empresa"><IconButton size="small" className={classes.remove} onClick={() => remove(sticker)}><DeleteOutline fontSize="small" /></IconButton></Tooltip>}
          <img className={classes.image} src={sticker.url} alt={sticker.name} onClick={() => choose(sticker)} />
          <Button size="small" startIcon={<Send />} onClick={() => choose(sticker)}>Enviar</Button>
        </Box>)}
      </Box>
      {!loading && !stickers.length && <Typography align="center" color="textSecondary" style={{ marginTop: 20 }}>Nenhuma figurinha cadastrada para esta empresa.</Typography>}
    </DialogContent>
    <DialogActions><Button onClick={onClose}>Fechar</Button></DialogActions>
  </Dialog>;
}
